import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, Button, DialogContent, DialogTitle, InputAdornment, Typography } from '@mui/material';
import { MutationOptions } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import {
  UpdateInvoiceAmountDueMutation,
  UpdateInvoiceAmountDueMutationVariables,
  useInvoiceQuery,
  useUpdateInvoiceAmountDueMutation,
} from '../../../api/gql';
import { FormInput } from '../../../components/form/FormInput';
import { FullSizeLoader } from '../../../components/FullSizeLoader';
import { getCurrencyName, getPriceWithCurrencyString } from '../../../utils/currencies';
import { notify } from '../../../utils/notification';
import { fromCentesimal, toCentesimal } from '../../../utils/transform';

type UpdateAmountForm = { amountDue: number };

type Props = {
  invoiceId: string;
  transNs?: string;
  keyPrefix?: string;
  onSuccess?: MutationOptions<
    UpdateInvoiceAmountDueMutation,
    any,
    UpdateInvoiceAmountDueMutationVariables
  >['onSuccess'];
};

export const UpdateAmountDueModule = ({ transNs, keyPrefix, onSuccess, invoiceId }: Props) => {
  const { t } = useTranslation(transNs, { keyPrefix });

  const {
    control,
    reset,
    setFocus,
    handleSubmit,
    setError,
    formState: { isDirty },
  } = useForm<UpdateAmountForm>({
    resolver: yupResolver(
      yup.object({
        amountDue: yup.number().required().min(1),
      }),
    ),
  });

  const { data, isLoading } = useInvoiceQuery({ id: invoiceId ?? '' });

  useEffect(() => {
    if (!data) {
      return;
    }
    setFocus('amountDue');
    reset({ amountDue: fromCentesimal(data.invoice.amountDue ?? 0) });
  }, [data, reset, setFocus]);

  const { mutate, isPending: isMutating } = useUpdateInvoiceAmountDueMutation({
    onSuccess: (res, variables, context) => {
      onSuccess?.(res, variables, context);
      notify.positive({ message: t('notification.success') });
    },
    onError: (e) => {
      console.error(e);
      notify.negative({ message: t('notification.error') });
    },
  });

  const onSubmit = ({ amountDue }: UpdateAmountForm) => {
    const amountDueInCentesimal = toCentesimal(amountDue);
    if (!data?.invoice.amount) {
      return;
    }
    if (amountDueInCentesimal > data.invoice.amount) {
      setError('amountDue', {
        message: t('error.amountDueIsLargerThenInvoicePrice') ?? '',
      });
      return;
    }

    mutate({
      invoiceId: invoiceId,
      amountDue: amountDueInCentesimal,
    });
  };

  return (
    <>
      <DialogTitle>{t('title')}</DialogTitle>
      <DialogContent dividers sx={{ position: 'relative' }}>
        {isLoading ? <FullSizeLoader /> : null}
        <Typography>{t('contentText')}</Typography>
        <Typography fontWeight={'bold'} mt={4} align={'center'}>
          {t('invoice.price')}
        </Typography>
        <Typography mt={1} align={'center'}>
          {getPriceWithCurrencyString(fromCentesimal(data?.invoice.amount ?? 0), data?.invoice.currency)}
        </Typography>
        <Box mt={4}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormInput
              control={control}
              name={'amountDue'}
              label={t('leftToPay') + '*'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position={'end'}>{getCurrencyName(data?.invoice.currency)}</InputAdornment>
                ),
              }}
            />
            <Box mt={2} display={'flex'} flexDirection={'column'} gap={2}>
              <LoadingButton
                loading={isMutating}
                disabled={!isDirty}
                variant={'contained'}
                type={'submit'}
                size={'large'}
              >
                {t('action.submit')}
              </LoadingButton>
              <Button component={Link} to={'..'}>
                {t('action.cancel')}
              </Button>
            </Box>
          </form>
        </Box>
      </DialogContent>
    </>
  );
};
